import {
  all,
  call,
  delay,
  ForkEffect,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { setEnrollmentEventProfileComplete } from 'Containers/App/actions';
import {
  makeGetFeatureFlag,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectGlobalField,
  makeSelectJvpField,
} from 'Containers/App/selectors';
import { AlexIdUser } from 'Containers/App/types';
import { resolveElections } from 'Containers/App/utils';
import { Household, HouseholdMember, JVPEnrollmentPlan } from 'Types/entities';
import {
  apiCreateOrUpdateHousehold,
  getClaimsPayers,
  getDrugs,
  getDrugsByNdc,
  getElections,
  getHealthFundIncentiveSurvey,
  getNetworkPreferenceSurvey,
  getHousehold,
  getIntegratedUserProfile,
  getLocationsByZip,
  submitJvpClaimsForm,
} from 'Utils/api';
import * as API_TYPES from 'Utils/apiTypes';
import { Election, JVPEnrollmentEvent } from 'Utils/apiTypes';
import Logger from 'Utils/logger';

import {
  createOrUpdateHouseholdFailure,
  createOrUpdateHouseholdSuccess,
  getAlexIdUserProfileFailure,
  getAlexIdUserProfileSuccess,
  getHouseholdError,
  getHouseholdSuccess,
  getIncentiveSurveyFailure,
  getIncentiveSurveySuccess,
  getNetworkPreferenceSurveyFailure,
  getNetworkPreferenceSurveySuccess,
  searchClaimsPayersFailure,
  searchClaimsPayersSuccess,
  searchDrugsFailure,
  searchDrugsSuccess,
  searchLocationsFailure,
  searchLocationsSuccess,
  submitClaimsFormFailure,
  submitClaimsFormSuccess,
} from './actions';
import {
  CANCEL_DEBOUNCED_HOUSEHOLD_UPDATE,
  CLEAR_INCENTIVE_LOADING,
  CREATE_OR_UPDATE_HOUSEHOLD_REQUEST,
  CREATE_OR_UPDATE_HOUSEHOLD_SUCCESS,
  DEBOUNCED_HOUSEHOLD_UPDATE,
  GET_HOUSEHOLD_REQUEST,
  PREFILL_USER_PROFILE_REQUEST,
  SEARCH_CLAIMS_PAYERS_REQUEST,
  SEARCH_DRUGS_REQUEST,
  SEARCH_LOCATIONS_REQUEST,
  SET_INCENTIVE_LOADING,
  SUBMIT_CLAIMS_FORM,
} from './constants';
import {
  makeGetEnrollmentPlans,
  makeGetHasIncentives,
  makeGetHousehold,
  makeGetHouseholdId,
  makeGetSelectedEnrollmentEvent,
  makeGetSelectedEnrollmentEventId,
  makeGetSelectedPublicationKey,
  makeSelectClientSurvey,
  makeSelectProfileField,
} from './selectors';
import { MemberSectionConfigFields } from './types';

export function* getXhrHousehold(action) {
  const memberConfig: MemberSectionConfigFields = action.memberConfig;
  const householdId = yield select(makeGetHouseholdId());
  const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());

  try {
    const resp: Household = yield call(getHousehold, householdId, enrollmentEventId);
    yield put(getHouseholdSuccess(resp, memberConfig));
  } catch (err) {
    yield put(getHouseholdError(err));
  }
}

/* Location sagas */
export function* getXhrLocationsByZip(action) {
  try {
    const resp: API_TYPES.GetLocationsResponse = yield call(getLocationsByZip, action.zipcode);
    yield put(searchLocationsSuccess(resp));
  } catch (err) {
    yield put(searchLocationsFailure(err));
  }
}

/* Prescription sagas */

export function* getXhrDrugs(action) {
  try {
    const resp: API_TYPES.GetDrugsResponse = yield call(getDrugs, action.query);
    yield put(searchDrugsSuccess(resp));
  } catch (err) {
    yield put(searchDrugsFailure(err));
  }
}
const BCBS_SYNONYMS = ['bcbs', 'blue cross'];

const BCBS_PATTERNS = [
  /\bbcbs\b/,
  /\bblue\s*cross\b/,
  /\bblue\s*shield\b/,
  /\bblue\s*cross\s*blue\s*shield\b/,
  /\bblue\b/,
];

export function isBcbsQuery(query: string) {
  const q = query.toLowerCase().trim();
  return BCBS_PATTERNS.some((pattern) => pattern.test(q));
}

export function normalize(str: string) {
  return str.toLowerCase().replace(/\s+/g, '');
}

export function filterBcbsPayers(payers: any[]) {
  return payers.filter((p) => {
    if (!p?.name) return false;
    const name = normalize(p.name);
    return name.includes('bcbs') || name.includes('bluecross') || name.includes('blueshield');
  });
}

export function* fetchBcbsPayers() {
  const responses: API_TYPES.GetClaimsPayersResponse[] = yield all(
    BCBS_SYNONYMS.map((term) => call(getClaimsPayers, term)),
  );

  const allPayers = responses.flatMap((res) => res.payers || []);

  const uniqPayers = Array.from(
    new Map(allPayers.filter((p) => p?.payerId).map((p) => [p.payerId, p])).values(),
  );

  return uniqPayers;
}

export function rankBcbsPayers(payers: any[], query: string) {
  const q = normalize(query);

  return [...payers].sort((a, b) => {
    const aName = normalize(a.name);
    const bName = normalize(b.name);

    const aExact = aName.includes(q);
    const bExact = bName.includes(q);

    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;

    const aStarts = aName.startsWith(q);
    const bStarts = bName.startsWith(q);

    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;

    return aName.length - bName.length;
  });
}
export function shouldApplyRanking(query: string) {
  return normalize(query).length > 6;
}

export function* getXhrClaimsPayers(action: any) {
  try {
    const query = action.query.toLowerCase().trim();
    let result;

    if (isBcbsQuery(query)) {
      const bcbs = yield call(fetchBcbsPayers);
      const filtered = filterBcbsPayers(bcbs);

      result = shouldApplyRanking(query)
        ? rankBcbsPayers(filtered, query)
        : filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const resp: API_TYPES.GetClaimsPayersResponse = yield call(getClaimsPayers, action.query);
      result = resp.payers ?? [];
    }

    yield put(
      searchClaimsPayersSuccess({
        payers: result,
      }),
    );
  } catch (err) {
    yield put(searchClaimsPayersFailure(err));
  }
}

export function* submitXhrClaimsForm(action) {
  try {
    const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());
    const resp = yield call(() => submitJvpClaimsForm(action.data, enrollmentEventId));
    yield put(submitClaimsFormSuccess(resp));
  } catch (err) {
    yield put(submitClaimsFormFailure(err));
  }
}

export function* getXhrAlexIdUserProfile(action) {
  const memberConfig = action.memberConfig;
  const user: AlexIdUser = yield select(makeSelectGlobalField('user'));
  const jvpUser: API_TYPES.JVPUserWithDependents = yield select(makeSelectGlobalField('jvpUser'));
  const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());
  const publicationKey = yield select(makeGetSelectedPublicationKey());
  const clientSurveyConfig = yield select(makeSelectClientSurvey());
  const isReturnUser = yield select(makeSelectJvpField('isReturnUser'));
  const jvpEligibilityQuestions = yield select(makeSelectProfileField('jvpEligibilityQuestions'));
  const electionMembersEnabled = yield select(makeGetFeatureFlag('is_election_members_enabled'));

  const validClientSurveyQuestionIds: string[] = [];
  if (clientSurveyConfig?.properties) {
    validClientSurveyQuestionIds.push(...Object.keys(clientSurveyConfig.properties));
  }

  try {
    // Fetch household from Comm API (for now; future state should be JVP)
    const household: Household = yield call(() => getIntegratedUserProfile(user, enrollmentEventId));
    if (!household) {
      throw new Error('Comm API household returned undefined');
    }

    let drugDetails: API_TYPES.GetBatchDrugDetailsResponse | null = null;
    let elections: Election[] = [];

    try {
      if (enrollmentEventId) {
        // Fetch previous year selected plans
        const electionsResp = yield call(() => getElections(enrollmentEventId));
        const fetchedElections: Election[] = electionsResp?.elections;
        const currentElections: Election[] = yield select(makeSelectGlobalField('elections'));
        const isCoverageWaived: boolean = yield select(makeSelectCommercialField('isCoverageWaived'));

        // preserve Redux pre-selected plan when JVP returns waived-only due to custom plan selection.
        elections = resolveElections(fetchedElections, currentElections, isCoverageWaived);
      }

      if (household.members.some((member) => member.prescriptions)) {
        const ndcsForLookup = getFlatNdcArray(household.members);
        if (ndcsForLookup.length > 0) {
          drugDetails = yield call(() => getDrugsByNdc(ndcsForLookup));
        }
      }
    } catch (rxError) {
      // We will not prefill prescriptions if there is an error
      Logger.log(`Failed to fetch drug details by NDC: ${rxError}`);
    }
    yield put(
      getAlexIdUserProfileSuccess(
        household,
        publicationKey,
        validClientSurveyQuestionIds,
        jvpUser,
        drugDetails,
        memberConfig,
        elections,
        isReturnUser,
        jvpEligibilityQuestions,
        electionMembersEnabled,
      ),
    );
  } catch (err) {
    yield put(getAlexIdUserProfileFailure(err));
  }
}

function getFlatNdcArray(members: HouseholdMember[]): string[] {
  const ndcSet = new Set(members.flatMap((member) => member.prescriptions.map((rx) => rx.ndc)));
  return Array.from(ndcSet);
}

// get incentive survey request
export function* getXhrIncentiveSurveyRequest() {
  try {
    // Don't bother getting eligible incentives if customer has none configured
    const hasIncentives = yield select(makeGetHasIncentives());
    if (!hasIncentives) {
      yield put(getIncentiveSurveySuccess({ incentive_questions: [] }));
      return;
    }

    const householdId = yield select(makeGetHouseholdId());
    const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());
    const resp: API_TYPES.IncentiveSurveyResponse = yield call(
      getHealthFundIncentiveSurvey,
      householdId,
      enrollmentEventId,
    );
    yield put(getIncentiveSurveySuccess(resp));
  } catch (err) {
    yield put(getIncentiveSurveyFailure(err as Error));
  }
}

// No customer-config guard here on purpose: Django applies the feature gate and
// returns an empty list when it is off.
export function* getXhrNetworkPreferenceSurveyRequest() {
  try {
    const householdId = yield select(makeGetHouseholdId());
    const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());
    const resp: API_TYPES.NetworkPreferenceSurveyResponse = yield call(
      getNetworkPreferenceSurvey,
      householdId,
      enrollmentEventId,
    );
    yield put(getNetworkPreferenceSurveySuccess(resp));
  } catch (err) {
    yield put(getNetworkPreferenceSurveyFailure(err as Error));
  }
}

export function* createOrUpdateHousehold() {
  try {
    const household = yield select(makeGetHousehold());
    const enrollmentEvent: JVPEnrollmentEvent = yield select(makeGetSelectedEnrollmentEvent());
    const householdId = yield select(makeGetHouseholdId());
    const builderCustomerKey = yield select(makeSelectConfigField('builder_customer_key'));
    const isEnrollmentPlanEligibilityEnabled = yield select(
      makeGetFeatureFlag('is_enrollment_plan_eligibility_enabled'),
    );

    let householdBody = household;
    if (isEnrollmentPlanEligibilityEnabled) {
      const enrollmentPlans: JVPEnrollmentPlan[] = yield select(makeGetEnrollmentPlans());
      if (enrollmentPlans.length > 0) {
        householdBody = { ...household, enrollment_plans: enrollmentPlans };
      }
    }

    // if hh_id is passed, update household. otherwise, create a new household.
    const resp: API_TYPES.ItemReference = yield call(
      apiCreateOrUpdateHousehold,
      householdBody,
      householdId,
      enrollmentEvent?.id,
    );

    yield put(createOrUpdateHouseholdSuccess(resp, builderCustomerKey));

    if (enrollmentEvent) {
      // When household is created successfully,
      // update enrollment event setting 'profile_complete' to true
      const employeeId = yield select(makeSelectJvpField('employeeId'));
      const eventType = yield select(makeSelectJvpField('eventType')) || 'none';

      yield put(
        setEnrollmentEventProfileComplete(employeeId, enrollmentEvent.jv_publication_uuid, eventType, true),
      );
    }
  } catch (err: unknown) {
    yield put(createOrUpdateHouseholdFailure(err as Error));
  }
}

export function* debouncedHouseholdUpdate() {
  const householdId = yield select(makeGetHouseholdId());
  if (!householdId) return;

  // Set loading immediately so consumers see a single loading flag
  yield put({ type: SET_INCENTIVE_LOADING });

  const { cancelled } = yield race({
    completed: call(function* () {
      // Debounce — takeLatest cancels previous invocations, restarting the delay
      yield delay(1000);
      // Proceed with household update (calls API, dispatches success/failure)
      yield call(createOrUpdateHousehold);
    }),
    cancelled: take(CANCEL_DEBOUNCED_HOUSEHOLD_UPDATE),
  });

  if (cancelled) {
    yield put({ type: CLEAR_INCENTIVE_LOADING });
  }
}

export default function* getProfileSaga() {
  // Determine whether to prepopulate profile forms
  const prepopulateProfile = yield select(makeSelectConfigField('prepopulate_profile'));
  const builderCustomerKey = yield select(makeSelectConfigField('builder_customer_key'));
  const sagas: ForkEffect[] = [];

  if (prepopulateProfile) {
    sagas.push(takeLatest(GET_HOUSEHOLD_REQUEST, getXhrHousehold));
  }

  sagas.push(takeLatest(SEARCH_LOCATIONS_REQUEST, getXhrLocationsByZip));
  sagas.push(takeLatest(SEARCH_DRUGS_REQUEST, getXhrDrugs));
  sagas.push(takeLatest(SEARCH_CLAIMS_PAYERS_REQUEST, getXhrClaimsPayers));
  sagas.push(takeLatest(SUBMIT_CLAIMS_FORM, submitXhrClaimsForm));

  sagas.push(takeLatest(PREFILL_USER_PROFILE_REQUEST, getXhrAlexIdUserProfile));

  sagas.push(takeLatest(CREATE_OR_UPDATE_HOUSEHOLD_REQUEST, createOrUpdateHousehold));
  sagas.push(takeLatest(DEBOUNCED_HOUSEHOLD_UPDATE, debouncedHouseholdUpdate));

  if (builderCustomerKey) {
    sagas.push(takeEvery(CREATE_OR_UPDATE_HOUSEHOLD_SUCCESS, getXhrIncentiveSurveyRequest));
    sagas.push(takeEvery(CREATE_OR_UPDATE_HOUSEHOLD_SUCCESS, getXhrNetworkPreferenceSurveyRequest));
  }
  yield all(sagas);
}
